// About APT Data - Organization story, mission, leadership, impact, partnerships
export const AboutAPTData = {
  mission: {
    pageTitle: "Our Mission & Values",
    tagline: "Building Community Strength Through Opportunity and Advocacy",
    foundingStory: {
      year: 2013,
      founder: "Emi Gjoni",
      origin:
        "Founded in 2013 by Emi Gjoni, Alisar Prospects Training emerged from a deep commitment to addressing educational and employment barriers facing London's most marginalized communities. What began as small-scale skills training has evolved into a holistic community organization supporting hundreds of individuals and families annually.",
    },
    missionStatement:
      "We exist to empower individuals and strengthen communities by providing accessible education, employment support, and advocacy. We believe everyone deserves the opportunity to thrive, regardless of their background, immigration status, or circumstances.",
    fourPillarApproach: [
      {
        pillar: "Education & Skills Development",
        description:
          "From ESOL for new arrivals to vocational qualifications, we provide learning opportunities that open doors to employment and integration.",
        examples: [
          "ESOL programs for 60+ learners",
          "Functional Skills (100+ annually)",
          "Industry-recognized vocational qualifications",
        ],
      },
      {
        pillar: "Employment & Economic Empowerment",
        description:
          "Personalized support to navigate the job market, secure employment, and build sustainable careers.",
        examples: [
          "CV and interview preparation",
          "Job search and employer connections",
          "45.7% employment success rate",
          "Long-term career planning",
        ],
      },
      {
        pillar: "Advocacy & Rights Protection",
        description:
          "Fighting for fair treatment and helping community members navigate complex systems (immigration, housing, benefits, healthcare).",
        examples: [
          "170+ advocacy cases in 2024-25",
          "£45,000+ in benefits secured for clients",
          "Housing crisis intervention",
          "Immigration and asylum support",
        ],
      },
      {
        pillar: "Community Building & Integration",
        description:
          "Creating connections, reducing isolation, and fostering belonging through outreach, family support, and community activities.",
        examples: [
          "350+ community members reached",
          "Family support programs",
          "Asylum seeker integration services",
          "Partnerships across faith and community organizations",
        ],
      },
    ],
    coreValues: [
      {
        value: "Innovation",
        description:
          "We adapt to meet emerging community needs, pioneering responsive programs that address real challenges.",
      },
      {
        value: "Enablement",
        description:
          "We don't create dependency - we equip people with skills, knowledge, and confidence to advocate for themselves and build their futures.",
      },
      {
        value: "Fairness & Access",
        description:
          "We remove barriers to opportunity. Services are free or low-cost, multilingual, and designed around community needs, not bureaucratic convenience.",
      },
      {
        value: "Inclusion",
        description:
          "We welcome everyone - asylum seekers, refugees, migrants, long-term residents. Diversity is our strength, and every person's dignity is non-negotiable.",
      },
    ],
    operationalTransparency: {
      legalStatus: "Community Interest Company (CIC)",
      governance: "Community-led board with lived experience representation",
      annualIncome: "Under £80,000 (2023-24)",
      fundingSources: [
        "Greater London Authority (GLA) - Community Outreach Programme",
        "Local authority contracts (Barking & Dagenham coding camps)",
        "Grant funding from trusts and foundations",
        "Small earned income from training contracts",
      ],
      financialPrinciple:
        "Every pound received goes directly to community benefit - minimal overheads, no profit extraction as CIC.",
    },
    geographicCoverage: {
      primaryAreas: ["Newham", "Barking & Dagenham", "Tower Hamlets"],
      widerReach: "Greater London, with particular focus on East London",
      outreachModel:
        "We meet communities where they are - temporary accommodation, community centers, faith spaces, not just our office.",
    },
  },

  leadership: {
    pageTitle: "Leadership & Team",
    founder: {
      name: "Emi Gjoni",
      role: "Founder & Director",
      since: 2013,
      story: {
        background:
          "Emi Gjoni founded Alisar Prospects Training in 2013, driven by firsthand understanding of the barriers marginalized communities face in accessing education and employment. With over a decade of experience in adult education, community development, and advocacy, Emi recognized that traditional services often failed to reach those who needed them most.",
        vision:
          "Emi's vision was to create an organization that didn't just deliver courses, but walked alongside individuals and families through their journeys - understanding that securing employment requires addressing housing instability, that learning English requires childcare support, and that career progression requires advocacy against discrimination.",
        leadership:
          "Under Emi's leadership, APT has grown from a single training room to a multi-faceted community organization reaching 750+ people annually. Emi's approach is relational, not transactional - building trust with communities often let down by institutions, and ensuring APT remains accountable to those it serves.",
        recognition:
          "Emi's work has been recognized through partnerships with the GLA, Newham College, and local authorities. But the true measure of success, Emi says, is when former clients become volunteers, staff, and community advocates themselves.",
      },
    },
    teamApproach: {
      description:
        "APT's team combines professional expertise with deep community connection. Many staff and volunteers have personal experience of migration, asylum, career change, or navigating UK systems as outsiders.",
      teamComposition: [
        "Qualified ESOL and Functional Skills tutors",
        "Accredited careers advisers and assessors",
        "Community advocates with legal and housing expertise",
        "Multilingual support staff (Arabic, Albanian, Bengali, Urdu, Tigrinya)",
        "Administrative and operations team",
        "Volunteer pool of 15+ community members",
      ],
      livedExperience:
        "We intentionally hire and train people from the communities we serve. Former learners become teaching assistants. Former clients become peer advocates. This isn't tokenism - it's recognition that expertise includes lived experience.",
    },
    volunteerLeadership: {
      description:
        "Community members contribute leadership through volunteering - from interpreting to peer mentoring to co-designing programs.",
      roles: [
        "Peer mentors for new ESOL learners",
        "Community language interpreters",
        "Outreach ambassadors to faith and cultural communities",
        "Guest speakers sharing success stories",
        "Programme feedback and co-design groups",
      ],
      impact:
        "15+ active volunteers, many of whom began as clients. This creates sustainable community capacity beyond APT's direct delivery.",
    },
  },

  impact: {
    pageTitle: "Our Impact: 2024-25 Results",
    overview:
      "These numbers represent real people - mothers who can now support their children's education, young people who've found career paths, families who've navigated crises with our advocacy. Behind every statistic is a human story.",
    dashboard: {
      heading: "Our Impact in Numbers",
      period: "2024-25",
      description:
        "These numbers represent real people - mothers, fathers, young people, and families across London who we've supported on their journeys.",
      summaryMetrics: [
        {
          value: "750+",
          label: "Community Members Reached",
        },
        {
          value: "170+",
          label: "Advocacy Support Cases",
        },
        {
          value: "45+",
          label: "People Into Employment",
        },
        {
          value: "100+",
          label: "Qualifications Achieved",
        },
        {
          value: "150+",
          label: "ESOL Learners Supported",
        },
        {
          value: "30+",
          label: "Young People Supported",
        },
      ],
      categories: [
        {
          id: "education-outcomes",
          title: "Education Outcomes",
          description:
            "Qualifications and learning achievements that open doors to new opportunities.",
          cards: [
            {
              value: "65+",
              label: "Functional Skills Maths",
              description: "Qualifications achieved",
            },
            {
              value: "35+",
              label: "Functional Skills English",
              description: "Qualifications achieved",
            },
            {
              value: "150+",
              label: "ESOL Learners",
              description: "Enrolled in programs",
            },
            {
              value: "80+",
              label: "Vocational Qualifications",
              description: "Certificates awarded",
            },
          ],
        },
        {
          id: "employment-success",
          title: "Employment Success",
          description:
            "Our holistic support addresses real barriers, resulting in significantly higher success rates than standard employment programs.",
          cards: [
            {
              value: "45.7%",
              label: "Job Success Rate",
              comparison: "vs 15-20% national average",
              highlight: true,
            },
            {
              value: "35",
              label: "Job Seekers Supported",
              description: "Active participants",
            },
            {
              value: "16",
              label: "Jobs Secured",
              description: "In 2024-25",
            },
            {
              value: "75%",
              label: "6-Month Retention",
              description: "Still employed",
            },
          ],
        },
        {
          id: "advocacy-impact",
          title: "Advocacy Impact",
          description:
            "Supporting community members through complex systems and crises - 170+ cases in 2024-25.",
          cards: [
            {
              value: "50+",
              label: "Benefits Advocacy",
              description: "Benefits advocacy cases",
            },
            {
              value: "35+",
              label: "Housing Support",
              description: "Housing-related cases",
            },
            {
              value: "40+",
              label: "Immigration Help",
              description: "Asylum/immigration cases",
            },
            {
              value: "25+",
              label: "Healthcare Access",
              description: "Healthcare access cases",
            },
            {
              value: "50+",
              label: "Education Support",
              description: "Education/SEND cases",
            },
          ],
        },
      ],
    },
    keyMetrics: {
      communityReach: {
        totalReached: "750+",
        description: "Community members engaged through all programs",
      },
      activeParticipants: {
        total: "200+",
        description: "Individuals in ongoing education or support programs",
      },
      educationOutcomes: {
        totalQualifications: "100+",
        breakdown: {
          mathsCompletions: "65+ Functional Skills Maths qualifications",
          englishCompletions: "35+ Functional Skills English qualifications",
          esolLearners: "60+ enrolled in ESOL programs",
          vocationalCerts: "25+ vocational qualifications awarded",
        },
      },
      employmentOutcomes: {
        jobsSeeking: 35,
        jobsSecured: 16,
        successRate: "45.7%",
        context:
          "Industry average for long-term unemployed/asylum seekers is 15-20%. Our success reflects wraparound support addressing real barriers.",
        retentionRate: "75% still employed after 6 months",
      },
      advocacySupport: {
        totalCases: "170+",
        breakdown: {
          benefits: "50+ benefits advocacy cases",
          housing: "35+ housing-related cases",
          immigration: "40+ asylum/immigration cases",
          healthcare: "25+ healthcare access cases",
          education: "50+ education/SEND cases",
        },
        financialImpact: "£45,000+ in backdated benefits secured for clients",
      },
      familySupport: {
        familiesSupported: "100+",
        childrenReached: "130+ children in activities and support",
        parentEducation: "60+ parents in education/parenting programs",
      },
    },

    demographicBreakdown: {
      note: "We serve diverse communities, with sensitivity to intersecting identities and needs",
      immigrationStatus: {
        asylumSeekers: "120+ asylum seekers and refugees",
        establishedMigrants: "100+ established migrants (EU and non-EU)",
        ukCitizens: "80+ UK-born citizens facing barriers",
        mixedStatus: "50+ mixed-status families",
      },
      genderBreakdown: {
        women: "60% (particularly single mothers and asylum-seeking women)",
        men: "35%",
        nonBinary: "5% prefer not to say / non-binary",
      },
      ageGroups: {
        youth: "15+ young people (16-25)",
        workingAge: "300+ adults (25-55)",
        olderAdults: "35+ over 55 (particularly digital inclusion)",
      },
      languageSupport: {
        description: "Services delivered in or with interpretation support",
        languages: [
          "English",
          "Arabic",
          "Albanian",
          "Bengali",
          "Urdu",
          "Tigrinya",
          "Farsi",
        ],
      },
    },

    qualitativeImpact: [
      {
        indicator: "Increased Confidence",
        evidence:
          "Self-reported confidence surveys show 80%+ of learners report increased confidence in their abilities and futures",
      },
      {
        indicator: "Reduced Isolation",
        evidence:
          "Peer support groups and community activities create lasting friendships and support networks - 90%+ report feeling more connected",
      },
      {
        indicator: "Family Stability",
        evidence:
          "Advocacy preventing evictions, securing benefits, accessing healthcare creates stability for children and families",
      },
      {
        indicator: "Community Leadership",
        evidence:
          "15+ former clients now volunteers, 3 hired as staff - building sustainable community capacity",
      },
      {
        indicator: "Intergenerational Impact",
        evidence:
          "Parents in ESOL can support children's homework; family learning strengthens bonds; youth programs inspire career aspirations",
      },
    ],

    comparativeBenchmarks: {
      employmentRate: {
        apt: "45.7%",
        nationalAverage: "15-20% for similar cohorts",
        context:
          "Our holistic support addressing childcare, transport, advocacy significantly outperforms standard employment programs",
      },
      courseCompletion: {
        apt: "78-85% depending on course",
        nationalAverage: "60-65% for adult education",
        context:
          "Flexible attendance, childcare support, and relational approach reduce dropout",
      },
    },

    whatSuccessLooksLike: {
      description:
        "Success isn't just employment numbers. It's the asylum-seeking mother who can read her child's school letters. The long-term unemployed father who walks into an interview with confidence. The isolated grandmother video-calling grandchildren abroad. The family no longer facing eviction because we fought for them.",
      principles: [
        "We measure what matters to communities, not just what's easy to count",
        "Short-term outcomes (qualifications) matter less than long-term impact (sustained employment, family stability, community integration)",
        "Individual success strengthens whole communities - former clients supporting new arrivals creates lasting change",
      ],
    },
  },

  partnerships: {
    pageTitle: "Partnerships & Collaborations",
    philosophy:
      "We can't meet all community needs alone. Strategic partnerships amplify impact, bring specialized expertise, and ensure holistic support for individuals and families.",
    educationPartners: [
      {
        organization: "Newham College",
        partnership:
          "Accredited ESOL and Functional Skills delivery partnership",
        impact:
          "Enables APT to offer nationally recognized qualifications with college's accreditation and quality assurance",
        duration: "5+ years",
      },
      {
        organization: "Highfield Awarding Body",
        partnership:
          "Accreditation for vocational qualifications (Customer Service, Health & Social Care, Advice & Guidance)",
        impact: "Ofqual-regulated qualifications recognized by employers",
        status: "Approved center",
      },
    ],
    fundingPartners: [
      {
        organization: "Greater London Authority (GLA)",
        program: "Community Outreach Programme",
        focus: "Skills and Employment Support for underserved Londoners",
        impact: "Core funding enabling free services for 200+ participants",
        grantPeriod: "2024-25",
      },
      {
        organization: "Barking & Dagenham Council",
        program: "Young People's Coding Camp",
        focus: "Digital skills for young people",
        impact: "Free summer coding camp for 25+ young people",
        status: "Recurring contract",
      },
    ],
    communityPartners: [
      {
        organization: "Integration Hubs (Refugee and Migrant Forum of Essex and London)",
        partnership: "Referrals and collaborative outreach to asylum seekers",
        impact: "Connecting isolated asylum seekers to education and support",
      },
      {
        organization: "Newham Law Centre",
        partnership: "Legal advice surgeries and immigration case referrals",
        impact: "Free legal support for 50+ community members annually",
      },
      {
        organization: "Citizens Advice",
        partnership: "Benefits advocacy and debt advice referrals",
        impact: "Specialist support complementing APT's advocacy",
      },
      {
        organization: "Local Faith Organizations",
        partnership:
          "Mosques, churches, temples as outreach sites and referral sources",
        impact: "Culturally trusted entry points reaching 50+ community members",
      },
    ],
    employerPartners: [
      {
        category: "Care Sector",
        employers: ["Local care homes", "Home care agencies", "NHS trusts"],
        collaboration:
          "Job opportunities for Health & Social Care graduates, work placements",
      },
      {
        category: "Retail & Hospitality",
        employers: ["Local retailers", "Hospitality businesses"],
        collaboration:
          "Entry-level opportunities for Customer Service learners",
      },
      {
        category: "Social Enterprises",
        employers: ["Community organizations", "Charities"],
        collaboration:
          "Values-aligned employers offering supportive work environments",
      },
    ],
    advocacyPartners: [
      {
        organization: "Shelter",
        focus: "Housing rights and homelessness prevention",
      },
      {
        organization: "Refugee Council",
        focus: "Asylum seeker support and immigration advice",
      },
      {
        organization: "Mind / Rethink Mental Illness",
        focus: "Mental health support for community members",
      },
    ],
    partnerWithUs: {
      description:
        "We welcome partnerships with organizations sharing our values and commitment to community empowerment.",
      opportunities: [
        {
          type: "Employers",
          ask: "Offer flexible opportunities, work placements, or apprenticeships for our learners - particularly those with non-traditional backgrounds",
        },
        {
          type: "Education Providers",
          ask: "Collaborate on course delivery, progression pathways, or shared accreditation",
        },
        {
          type: "Community Organizations",
          ask: "Co-locate services, cross-refer clients, deliver joint outreach",
        },
        {
          type: "Funders",
          ask: "Support unrestricted or project-based funding to expand reach and sustain services",
        },
        {
          type: "Pro Bono Professionals",
          ask: "Lawyers, accountants, HR professionals offering skills-based volunteering",
        },
      ],
      contactInfo:
        "Interested in partnership? Contact us to discuss how we can collaborate for community benefit.",
    },
  },
};

// Quick reference for navigation
export const AboutAPTCategories = [
  {
    id: "mission-values",
    title: "Our Mission & Values",
    url: "/about/mission-values",
  },
  {
    id: "leadership-team",
    title: "Leadership Team",
    url: "/about/leadership-team",
  },
  {
    id: "our-impact",
    title: "Our Impact",
    url: "/about/our-impact",
  },
  {
    id: "partnerships",
    title: "Partnerships",
    url: "/about/partnerships",
  },
];
